import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, PlayCircle, Download, Target } from "lucide-react";

interface CoverageStats {
  protocols_total: number;
  metrics_points_total: number;
}

interface ProtocolWithoutMetrics {
  llama_slug: string;
  app_name: string;
  chain: string | null;
  tags: string[];
}

export default function AdminCoverage() {
  const [stats, setStats] = useState<CoverageStats>({ protocols_total: 0, metrics_points_total: 0 });
  const [protocols, setProtocols] = useState<ProtocolWithoutMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [backfillSlugs, setBackfillSlugs] = useState("");
  const [backfillDays, setBackfillDays] = useState("7");
  const [discoveryOffset, setDiscoveryOffset] = useState(() => 
    parseInt(localStorage.getItem('discovery-offset') || '0')
  );
  const [discoveryLimit, setDiscoveryLimit] = useState(() => 
    parseInt(localStorage.getItem('discovery-limit') || '400')
  );
  const [ingestOffset, setIngestOffset] = useState(() => 
    parseInt(localStorage.getItem('ingest-offset') || '0')
  );
  const [ingestLimit, setIngestLimit] = useState(() => 
    parseInt(localStorage.getItem('ingest-limit') || '100')
  );
  const [lastDiscoveryResult, setLastDiscoveryResult] = useState<any>(null);
  const [lastIngestResult, setLastIngestResult] = useState<any>(null);
  const [lastBackfillResult, setLastBackfillResult] = useState<any>(null);
  const [lastBatchResult, setLastBatchResult] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Get protocol stats
      const { count: protocolCount } = await supabase
        .from('protocol_map')
        .select('*', { count: 'exact', head: true });

      // Get metrics stats
      const { count: metricsCount } = await supabase
        .from('metrics')
        .select('*', { count: 'exact', head: true });

      setStats({
        protocols_total: protocolCount || 0,
        metrics_points_total: metricsCount || 0
      });

      // Get protocols without metrics (up to 200)
      const { data: protocolsWithoutMetrics } = await supabase
        .from('protocol_map')
        .select('llama_slug, app_name, chain, tags')
        .limit(200);

      setProtocols(protocolsWithoutMetrics || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Erro ao carregar dados",
        description: "Não foi possível carregar os dados de cobertura.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDiscovery = async () => {
    try {
      setActionLoading("discovery");
      
      // Save parameters to localStorage
      localStorage.setItem('discovery-offset', discoveryOffset.toString());
      localStorage.setItem('discovery-limit', discoveryLimit.toString());
      
      const { data: result, error } = await supabase.functions.invoke('scheduled-tasks', {
        body: { 
          task_type: 'coverage-discovery',
          parameters: {
            offset: discoveryOffset,
            limit: discoveryLimit
          }
        }
      });

      if (error) {
        throw new Error(error.message || 'Erro na descoberta');
      }
      
      // Handle WORKER_LIMIT error specifically
      if (result?.ok === false && result?.reason === 'WORKER_LIMIT') {
        toast({
          title: "Limite de Processamento Atingido",
          description: result.hint || "Reduza o limite (ex.: 250) e tente novamente.",
          variant: "destructive"
        });
        return;
      }
      
      // Store the result for display
      setLastDiscoveryResult(result);
      
      toast({
        title: "Descoberta concluída",
        description: `${result?.processed || 0} processados, ${result?.upserted || 0} atualizados. Next offset: ${result?.next_offset || 'fim'}`
      });
      
      // If there's a next_offset, update the UI
      if (result?.next_offset !== null && result?.next_offset !== undefined) {
        setDiscoveryOffset(result.next_offset);
      }
      
      await loadData();
    } catch (error) {
      console.error('Discovery error:', error);
      toast({
        title: "Erro na descoberta",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleIngest = async () => {
    try {
      setActionLoading("ingest");
      
      // Save parameters to localStorage
      localStorage.setItem('ingest-offset', ingestOffset.toString());
      localStorage.setItem('ingest-limit', ingestLimit.toString());
      
      const { data: result, error } = await supabase.functions.invoke('scheduled-tasks', {
        body: { 
          task_type: 'metrics-ingest',
          parameters: {
            offset: ingestOffset,
            limit: ingestLimit
          }
        }
      });

      if (error) {
        throw new Error(error.message || 'Erro na coleta de métricas');
      }
      
      // Store the result for display
      setLastIngestResult(result);
      
      toast({
        title: "Coleta concluída",
        description: `${result?.collected || 0} métricas coletadas de ${result?.processed || 0} protocolos processados.`
      });
      
      await loadData();
    } catch (error) {
      console.error('Ingest error:', error);
      toast({
        title: "Erro na coleta",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleBackfill = async () => {
    try {
      setActionLoading("backfill");
      
      const slugsArray = backfillSlugs.split('\n').filter(s => s.trim()).map(s => s.trim());
      
      if (slugsArray.length === 0) {
        toast({
          title: "Erro",
          description: "Por favor, insira pelo menos um slug.",
          variant: "destructive"
        });
        return;
      }

      const { data: result, error } = await supabase.functions.invoke('metrics-backfill-run', {
        body: { 
          slugs: slugsArray,
          days: parseInt(backfillDays),
          batch_id: `manual_backfill_${Date.now()}`
        }
      });

      if (error) {
        throw new Error(error.message || 'Erro no backfill');
      }

      setLastBackfillResult(result);
      
      toast({
        title: "Backfill concluído",
        description: `${result?.collected || 0} métricas coletadas de ${result?.processed || 0} protocolos processados.`
      });
      
      setBackfillSlugs("");
      await loadData();
    } catch (error) {
      console.error('Backfill error:', error);
      toast({
        title: "Erro no backfill",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleBatchProcessAll = async () => {
    try {
      setActionLoading("batch-all");
      
      const { data: result, error } = await supabase.functions.invoke('scheduled-tasks', {
        body: { 
          task_type: 'batch-process-all',
          parameters: {
            batch_size: 100,
            max_batches: 10
          }
        }
      });

      if (error) {
        throw new Error(error.message || 'Erro no processamento em lote');
      }

      setLastBatchResult(result);
      
      toast({
        title: "Processamento em lote concluído",
        description: `${result?.total_collected || 0} métricas coletadas em ${result?.batches_executed || 0} lotes.`
      });
      
      await loadData();
    } catch (error) {
      console.error('Batch process error:', error);
      toast({
        title: "Erro no processamento em lote",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAutoBackfill = async () => {
    try {
      setActionLoading("auto-backfill");
      
      const { data: result, error } = await supabase.functions.invoke('scheduled-tasks', {
        body: { 
          task_type: 'metrics-backfill',
          parameters: {
            target_count: 20,
            days: 30
          }
        }
      });

      if (error) {
        throw new Error(error.message || 'Erro no backfill automático');
      }

      setLastBackfillResult(result);
      
      toast({
        title: "Backfill automático concluído", 
        description: `${result?.collected || 0} métricas coletadas de ${result?.processed || 0} protocolos com menos dados.`
      });
      
      await loadData();
    } catch (error) {
      console.error('Auto backfill error:', error);
      toast({
        title: "Erro no backfill automático",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cobertura de Protocolos</h1>
          <p className="text-muted-foreground">
            Gerencie a descoberta e ingestão de dados de protocolos DeFi.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Protocolos</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.protocols_total.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Protocolos mapeados no sistema
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pontos de Métricas</CardTitle>
            <Download className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.metrics_points_total.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Total de dados históricos coletados
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Discovery Parameters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Parâmetros de Descoberta</CardTitle>
          <CardDescription>
            Configure os parâmetros para a descoberta de protocolos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3 items-end">
            <div className="space-y-2">
              <Label htmlFor="discovery-offset">Offset</Label>
              <Input
                id="discovery-offset"
                type="number"
                min="0"
                step="1"
                value={discoveryOffset}
                onChange={(e) => setDiscoveryOffset(parseInt(e.target.value) || 0)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">Protocolo inicial</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="discovery-limit">Limit</Label>
              <Input
                id="discovery-limit"
                type="number"
                min="1"
                max="1000"
                step="1"
                value={discoveryLimit}
                onChange={(e) => setDiscoveryLimit(parseInt(e.target.value) || 400)}
                placeholder="400"
              />
              <p className="text-xs text-muted-foreground">Máximo por execução</p>
            </div>
            {lastDiscoveryResult && (
              <div className="space-y-2">
                <Label>Último Resultado</Label>
                <div className="text-sm space-y-1">
                  <div>Processados: <span className="font-mono">{lastDiscoveryResult.processed || 0}</span></div>
                  <div>Atualizados: <span className="font-mono">{lastDiscoveryResult.upserted || 0}</span></div>
                  <div>Total: <span className="font-mono">{lastDiscoveryResult.total || 0}</span></div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ingest Parameters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Parâmetros de Coleta de Métricas</CardTitle>
          <CardDescription>
            Configure os parâmetros para a coleta de métricas dos protocolos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3 items-end">
            <div className="space-y-2">
              <Label htmlFor="ingest-offset">Offset</Label>
              <Input
                id="ingest-offset"
                type="number"
                min="0"
                step="1"
                value={ingestOffset}
                onChange={(e) => setIngestOffset(parseInt(e.target.value) || 0)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">Protocolo inicial</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ingest-limit">Limit</Label>
              <Input
                id="ingest-limit"
                type="number"
                min="1"
                max="500"
                step="1"
                value={ingestLimit}
                onChange={(e) => setIngestLimit(parseInt(e.target.value) || 100)}
                placeholder="100"
              />
              <p className="text-xs text-muted-foreground">Máximo por execução</p>
            </div>
            {lastIngestResult && (
              <div className="space-y-2">
                <Label>Último Resultado</Label>
                <div className="text-sm space-y-1">
                  <div>Processados: <span className="font-mono">{lastIngestResult.processed || 0}</span></div>
                  <div>Coletados: <span className="font-mono">{lastIngestResult.collected || 0}</span></div>
                  <div>Salvos: <span className="font-mono">{lastIngestResult.upserted || 0}</span></div>
                  {lastIngestResult.errors > 0 && (
                    <div className="text-destructive">Erros: <span className="font-mono">{lastIngestResult.errors}</span></div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Batch Processing */}
      {lastBatchResult && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resultado do Processamento em Lote</CardTitle>
            <CardDescription>
              Último processamento automático executado.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="space-y-2">
                <Label>Lotes Executados</Label>
                <div className="text-2xl font-bold">{lastBatchResult.batches_executed || 0}</div>
              </div>
              <div className="space-y-2">
                <Label>Total Processados</Label>
                <div className="text-2xl font-bold">{lastBatchResult.total_processed || 0}</div>
              </div>
              <div className="space-y-2">
                <Label>Total Coletados</Label>
                <div className="text-2xl font-bold">{lastBatchResult.total_collected || 0}</div>
              </div>
              <div className="space-y-2">
                <Label>Lotes com Erro</Label>
                <div className="text-2xl font-bold text-destructive">{lastBatchResult.batch_errors || 0}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backfill Results */}
      {lastBackfillResult && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resultado do Backfill</CardTitle>
            <CardDescription>
              Último backfill executado.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="space-y-2">
                <Label>Protocolos Processados</Label>
                <div className="text-2xl font-bold">{lastBackfillResult.processed || 0}</div>
              </div>
              <div className="space-y-2">
                <Label>Métricas Coletadas</Label>
                <div className="text-2xl font-bold">{lastBackfillResult.collected || 0}</div>
              </div>
              <div className="space-y-2">
                <Label>Batch ID</Label>
                <div className="text-xs font-mono">{lastBackfillResult.batch_id || 'N/A'}</div>
              </div>
              <div className="space-y-2">
                <Label>Erros</Label>
                <div className="text-2xl font-bold text-destructive">{lastBackfillResult.errors || 0}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4 flex-wrap">
        <Button 
          onClick={handleDiscovery}
          disabled={actionLoading === "discovery"}
          className="flex items-center gap-2"
        >
          {actionLoading === "discovery" && <Loader2 className="h-4 w-4 animate-spin" />}
          <PlayCircle className="h-4 w-4" />
          Descobrir Agora
        </Button>

        <Button 
          onClick={handleIngest}
          disabled={actionLoading === "ingest"}
          variant="secondary"
          className="flex items-center gap-2"
        >
          {actionLoading === "ingest" && <Loader2 className="h-4 w-4 animate-spin" />}
          <Download className="h-4 w-4" />
          Coletar Métricas
        </Button>

        <Button 
          onClick={handleBatchProcessAll}
          disabled={actionLoading === "batch-all"}
          variant="outline"
          className="flex items-center gap-2"
        >
          {actionLoading === "batch-all" && <Loader2 className="h-4 w-4 animate-spin" />}
          <PlayCircle className="h-4 w-4" />
          Processar Todos os Lotes
        </Button>

        <Button 
          onClick={handleAutoBackfill}
          disabled={actionLoading === "auto-backfill"}
          variant="outline"
          className="flex items-center gap-2"
        >
          {actionLoading === "auto-backfill" && <Loader2 className="h-4 w-4 animate-spin" />}
          <Target className="h-4 w-4" />
          Backfill Automático
        </Button>

        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Backfill Selecionados
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Backfill Selecionados</DialogTitle>
              <DialogDescription>
                Insira os slugs dos protocolos que deseja fazer backfill (um por linha).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="slugs">Slugs dos Protocolos</Label>
                <Textarea
                  id="slugs"
                  placeholder="uniswap&#10;aave&#10;compound"
                  value={backfillSlugs}
                  onChange={(e) => setBackfillSlugs(e.target.value)}
                  rows={6}
                />
              </div>
              <div>
                <Label htmlFor="days">Dias para Backfill</Label>
                <Input
                  id="days"
                  type="number"
                  min="1"
                  max="365"
                  value={backfillDays}
                  onChange={(e) => setBackfillDays(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                onClick={handleBackfill}
                disabled={actionLoading === "backfill"}
                className="w-full"
              >
                {actionLoading === "backfill" && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Executar Backfill
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Protocols Without Metrics Table */}
      <Card>
        <CardHeader>
          <CardTitle>Protocolos Sem Métricas</CardTitle>
          <CardDescription>
            Até 200 protocolos que ainda não possuem dados de métricas coletados.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Slug</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Chain</TableHead>
                  <TableHead>Tags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {protocols.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8">
                      <div className="text-muted-foreground">
                        Todos os protocolos possuem métricas coletadas.
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  protocols.map((protocol) => (
                    <TableRow key={protocol.llama_slug}>
                      <TableCell className="font-mono text-sm">
                        {protocol.llama_slug}
                      </TableCell>
                      <TableCell>{protocol.app_name || '-'}</TableCell>
                      <TableCell>
                        {protocol.chain ? (
                          <Badge variant="secondary" className="text-xs">
                            {protocol.chain}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {protocol.tags?.slice(0, 2).map((tag) => (
                            <Badge key={tag} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {protocol.tags?.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{protocol.tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}